/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ONE SCROLL LISTENER FOR THE WHOLE SITE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Two pieces of chrome need to know about scrolling — the header, which
 * condenses on the way down, and the back-to-top rail, which appears once
 * there is something to go back up from. Giving each of them its own
 * `useEffect` + `addEventListener` would put two handlers on the hottest event
 * the browser has. This is one handler, shared, started when the first
 * component subscribes and removed when the last one leaves.
 *
 * ⚠️ WHAT THIS PUBLISHES IS BOOLEANS, NOT A POSITION, AND THAT IS DELIBERATE.
 * `useSyncExternalStore` re-renders whenever the snapshot is a new value, so a
 * store that published `y` would re-render every subscriber on every scroll
 * frame — which is exactly the jank this is supposed to avoid. The snapshot
 * object is cached and only REPLACED when one of the three booleans actually
 * flips, which on a normal read is a handful of times per page.
 *
 * ⚠️ IT READS `window.scrollY` AND NOTHING ELSE. No `getBoundingClientRect`,
 * no `offsetTop`, nothing that forces layout — `scrollY` is a value the
 * browser already has. The listener is `passive`, so it can never delay a
 * scroll, and it is coalesced into one `requestAnimationFrame` so a
 * high-frequency trackpad cannot run the work more than once per frame.
 *
 * ⚠️ DIRECTION IS WHY THIS IS JAVASCRIPT AT ALL. A CSS `scroll()` timeline is
 * a pure function of POSITION and cannot tell up from down, so the condensing
 * header has no CSS-only form. Everything else on this site that reacts to
 * scrolling is `animation-timeline: view()` and involves no script whatever.
 */

export type ScrollState = {
  /** The reader is moving down the page. Hysteresis applied; see `DELTA`. */
  readonly down: boolean
  /** Still within the first screenful, where chrome is always fully expanded. */
  readonly atTop: boolean
  /** Far enough down that "back to top" is a real offer rather than clutter. */
  readonly deep: boolean
}

/** Below this the header never condenses — it also absorbs a rubber-band. */
const TOP_ZONE = 120

/**
 * The back-to-top rail appears after roughly one screen. It is expressed as a
 * fraction of the viewport rather than a pixel count so that a 640 px phone
 * and a 1440 px monitor both get it at the same point in the READING, not at
 * the same point in the document.
 */
const DEEP_FRACTION = 1.1

/** Ignore movement smaller than this, so a trackpad wobble cannot flap it. */
const DELTA = 8

let state: ScrollState = { down: false, atTop: true, deep: false }
const subscribers = new Set<() => void>()

let lastY = 0
let queued = false
let listening = false

const publish = (next: ScrollState): void => {
  if (next.down === state.down && next.atTop === state.atTop && next.deep === state.deep) return
  state = next
  for (const notify of subscribers) notify()
}

const update = (): void => {
  queued = false
  const y = window.scrollY
  const deep = y > window.innerHeight * DEEP_FRACTION

  if (y < TOP_ZONE) {
    publish({ down: false, atTop: true, deep })
    lastY = y
    return
  }

  if (Math.abs(y - lastY) < DELTA) {
    // Too small to be a direction, but `deep` may still have crossed.
    publish({ down: state.down, atTop: false, deep })
    return
  }

  publish({ down: y > lastY, atTop: false, deep })
  lastY = y
}

const onScroll = (): void => {
  if (queued) return
  queued = true
  requestAnimationFrame(update)
}

/** `useSyncExternalStore` subscribe. Attaches the one listener on demand. */
export const subscribeToScroll = (notify: () => void): (() => void) => {
  subscribers.add(notify)

  if (!listening) {
    listening = true
    lastY = window.scrollY
    window.addEventListener('scroll', onScroll, { passive: true })
    // Deep-linking or a restored scroll position means the page can start
    // part-way down, so the first state is computed rather than assumed.
    update()
  }

  return () => {
    subscribers.delete(notify)
    if (subscribers.size > 0) return
    listening = false
    window.removeEventListener('scroll', onScroll)
  }
}

/** `useSyncExternalStore` getSnapshot. Stable between real changes. */
export const getScrollState = (): ScrollState => state
